import {
  applySnapshot,
  flow,
  getSnapshot,
  SnapshotOrInstance,
  types,
} from "mobx-state-tree";
import { addDays, eachDayOfInterval } from "date-fns";
import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";
import api from "services/api";
import { reorder } from "utils/calculation";
import { ignoreTimezone } from "utils/date";
import { getNextPublishStatus } from "utils/publish";
import {
  ActiveProgram,
  WorkoutCategoryModel,
  PROGRAM_PERMISSION_TYPES,
  PUBLISH_STATUSES,
  WORKOUT_STRUCTURE_TYPES,
} from "constants/models";
import { REORDER_MOVE_TYPES } from "constants/reorder";
import { FILTER_COLUMNS } from "constants/filterConstants";
import { isEmptyObject } from "utils";
import { MSURL } from "../utils/ms";
const categoryPath = "/workouts/api/v1";

const CategoryRequest = types.model({
  title: types.string,
  subTitle: types.maybe(types.string),
  note: types.maybeNull(types.string),
  startDate: types.maybe(types.string),
  structureType: types.maybe(types.number),
  imageUrl: types.maybe(types.string),
  activePrograms: types.maybe(
    types.array(
      types.model({
        permission: types.string,
        id: types.number,
      }),
    ),
  ),
  familyIds: types.array(types.number),
  collectionsId: types.maybe(types.array(types.number)),
});

export const CategoryDetailsModel = types
  .model({
    id: types.number,
    title: types.string,
    subTitle: types.maybeNull(types.string),
    note: types.maybeNull(types.string),
    imageURL: types.maybeNull(types.string),
    activePrograms: types.array(ActiveProgram),
    startDate: types.maybeNull(types.string),
    structureType: types.enumeration(Object.values(WORKOUT_STRUCTURE_TYPES)),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
    cardImage: types.maybeNull(types.string),
    family: types.maybeNull(
      types.model({
        id: types.number,
        structureType: types.maybe(
          types.enumeration(Object.values(WORKOUT_STRUCTURE_TYPES)),
        ),
        name: types.maybeNull(types.string),
      }),
    ),
    collections: types.maybe(
      types.array(
        types.model({
          name: types.string,
          id: types.number,
        }),
      ),
    ),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
        isWeekStructureEnabled:
          self.structureType === WORKOUT_STRUCTURE_TYPES.WEEK,
        file: self.imageURL,
        cardFile: self.cardImage,
        activePrograms: self.activePrograms.map(({ id, permission }) => ({
          id,
          switchState: permission === PROGRAM_PERMISSION_TYPES.PREVIEW,
        })),
        startDate: self.startDate
          ? new Date(ignoreTimezone(self.startDate))
          : undefined,
        collection: self.collections?.at(0)?.id.toString(),
        structureType:
          self.family?.id === 7
            ? WORKOUT_STRUCTURE_TYPES.COLLECTION
            : self.structureType,
      };
    },

    get isWeekCategory() {
      return self.structureType === WORKOUT_STRUCTURE_TYPES.WEEK;
    },
  }))
  .views(self => ({
    get weekCategoryDays() {
      return self.isWeekCategory && self.startDate
        ? eachDayOfInterval({
            start: new Date(ignoreTimezone(self.startDate)),
            end: addDays(new Date(ignoreTimezone(self.startDate)), 6),
          })
        : [];
    },
  }));

const InAppStudioStore = createTableStore({
  fetch: flow(function*({ params }) {
    const res = yield api.get(
      `/workouts/api/v1/workout-categories/list`,
      params,
      undefined,
      MSURL,
    );

    return {
      count: res.data?.context?.totalRows ?? 0,
      items: res.data?.items ?? [],
      context: res.data?.context,
    };
  }),
  itemModel: WorkoutCategoryModel,
})
  .props({
    categoryDetails: types.maybe(CategoryDetailsModel),
    isCategoryLoading: types.optional(types.boolean, false),
    isCategoryManaging: types.optional(types.boolean, false),
    isCategoryReordering: types.optional(types.boolean, false),
    isCategoryPublishing: types.optional(types.boolean, false),
    isAllContentPublishing: types.optional(types.boolean, false),
    isMoreLoading: types.optional(types.boolean, false),
    isCategoryCopying: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo(): boolean {
      return (
        self.context?.page != null &&
        self.context?.pages != null &&
        self.context.page < self.context.pages
      );
    },
  }))
  .actions(self => ({
    getReorderInfo(
      reorderedItems: SnapshotOrInstance<typeof WorkoutCategoryModel>[],
      categoryId: number,
    ) {
      const categoryIdNewIndex = reorderedItems.findIndex(
        (item: SnapshotOrInstance<typeof WorkoutCategoryModel>) =>
          item.id === categoryId,
      );

      switch (categoryIdNewIndex) {
        case 0:
          return {
            afterId: undefined,
            moveType: REORDER_MOVE_TYPES.TOP_OF_PAGE,
          };
        case self.items.length - 1:
          return {
            afterId: undefined,
            moveType: REORDER_MOVE_TYPES.BOTTOM_OF_PAGE,
          };
        default:
          return {
            afterId: reorderedItems[categoryIdNewIndex - 1].id,
            moveType: REORDER_MOVE_TYPES.NORMAL,
          };
      }
    },

    updateCategory(id: number, data: any) {
      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        applySnapshot(self.items[index], {
          ...getSnapshot(self.items[index]),
          ...data,
        });
      }
    },
  }))
  .actions(self => ({
    fetchMoreCategories: flow(function*() {
      self.isMoreLoading = true;
      try {
        const currentPage = self.context?.page ?? 1;
        const totalPages = self.context?.pages ?? 1;

        if (currentPage < totalPages) {
          const nextPage = currentPage + 1;
          const response = yield api.get(
            `/workouts/api/v1/workout-categories/list`,
            {
              page: nextPage,
              size: DEFAULT_PAGE_SIZE,
              ...self.filters,
            },
            undefined,
            MSURL,
          );

          if (response.success) {
            const newItems = response.data.items.map(item =>
              WorkoutCategoryModel.create({
                ...item,
                structureType:
                  item.structureType || WORKOUT_STRUCTURE_TYPES.STANDARD,
              }),
            );

            self.items.push(...newItems);
            self.context = response.data.context;
          }
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    fetchCategoryDetails: flow(function*(id: number) {
      self.isCategoryLoading = true;

      try {
        let categoryDetails = yield api.get(
          `/workouts/api/v1/workout-categories/${id}`,
          undefined,
          undefined,
          MSURL,
        );
        categoryDetails = categoryDetails.data;
        applySnapshot(self, {
          ...self,
          categoryDetails,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryLoading = false;
      }
    }),

    createCategory: flow(function*(values: any) {
      self.isCategoryManaging = true;
      const payload = CategoryRequest.create(values);
      try {
        const response = yield api.post(
          `/workouts/api/v1/workout-categories`,
          payload,
          undefined,
          MSURL,
        );

        return response;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryManaging = false;
      }
    }),

    editCategoryInfo: flow(function*(id: number, values: any) {
      self.isCategoryManaging = true;
      const payload = CategoryRequest.create(values);
      try {
        const response = yield api.put(
          `/workouts/api/v1/workout-categories/${id}`,
          payload,
          undefined,
          MSURL,
        );
        return response;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryManaging = false;
      }
    }),

    editCategoryImage: flow(function*(payload: any) {
      self.isCategoryManaging = true;

      try {
        return yield api.putFormData(
          `/workout/category/${payload.id}/photo/upload`,
          payload,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryManaging = false;
      }
    }),

    reorderCategories: flow(function*({
      startIndex,
      endIndex,
    }: {
      startIndex: number;
      endIndex: number;
    }) {
      self.isCategoryReordering = true;
      const { items } = getSnapshot(self);

      try {
        const categoryId = items[startIndex].id;
        const reorderedItems = reorder(items, startIndex, endIndex);
        applySnapshot(self, { ...self, items: reorderedItems });

        const reorderInfo = self.getReorderInfo(reorderedItems, categoryId);

        yield api.post(`/workout/category/move`, {
          id: categoryId,
          ...reorderInfo,
        });
      } catch (error) {
        console.log("error :>> ", error);
        applySnapshot(self, { ...self, items });
        throw error;
      } finally {
        self.isCategoryReordering = false;
      }
    }),

    archiveCategory: flow(function*(id: number) {
      self.isCategoryManaging = true;

      try {
        return yield api.post(`/workout/category/${id}/archive`, {});
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryManaging = false;
      }
    }),

    changeCategoryPublishStatus: flow(function*(id: number, status: string) {
      self.isCategoryPublishing = true;

      try {
        const newStatus = getNextPublishStatus(status);
        if (newStatus) {
          const { success } = yield api.post(`/workout/category/${id}/status`, {
            status: newStatus,
          });
          if (success) {
            self.updateCategory(id, { publishStatus: newStatus });
          }
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryPublishing = false;
      }
    }),

    copyCategory: flow(function*(id: number) {
      self.isCategoryCopying = true;

      try {
        yield api.post(
          `/workouts/api/v1/workout-categories/${id}/copy`,
          {},
          undefined,
          MSURL,
        );
        yield self.fetch();
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryCopying = false;
      }
    }),

    fetchCardCategoryImage: flow(function*(id?: number) {
      try {
        self.isCategoryLoading = true;
        const cardImageDetail = yield api.get(
          `${categoryPath}/workout-categories/${id}/get-card-image`,
          undefined,
          undefined,
          MSURL,
        );
        applySnapshot(self, {
          ...self,
          categoryDetails: {
            ...self.categoryDetails,
            ...cardImageDetail,
          },
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryLoading = false;
      }
    }),

    createFamilyTagging: flow(function*(payload: any) {
      self.isCategoryManaging = true;

      try {
        yield api.put(
          `${categoryPath}/workout-categories/${payload.id}/family`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryManaging = false;
      }
    }),

    editCategoryCardImage: flow(function*(payload: any) {
      self.isCategoryManaging = true;

      try {
        return yield api.putFormData(
          `${categoryPath}/workout-categories/${payload.id}/upload-card-image`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryManaging = false;
      }
    }),

    resetCategoryDetails() {
      applySnapshot(self, { ...self, categoryDetails: undefined });
    },
  }));

export const InAppStudioArchivedStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/workout/category/archived/list`, params);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: WorkoutCategoryModel,
  searchBy: "search",
})
  .props({
    isManaging: types.optional(types.boolean, false),
    isMoreLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },

    get options() {
      const { items } = getSnapshot(self);
      return items.map(({ id, title, imageUrl, publishStatus }) => ({
        label: title,
        value: {
          id,
          imageUrl,
          title,
          publishStatus,
        },
      }));
    },
  }))
  .actions(self => ({
    fetchMore: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);

          const { items } = yield api.get(`/workout/category/archived/list`, {
            size: DEFAULT_PAGE_SIZE,
            page: self.page,
          });

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    restore: flow(function*(payload: any) {
      self.isManaging = true;

      try {
        return yield api.post(`/workout/category/restore`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isManaging = false;
      }
    }),
  }))
  .create();

export default InAppStudioStore.create();
